import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCandidateQuery } from "@/queries/candidate/candidate";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { GroupedMultiSelect, MultiSelect } from "@/components/ui/multi-select";

import {
  candidateIdealEngagementFormSchema,
  CandidateIdealEngagementFormSchema,
} from "./schema";
import {
  companyEmployeesList,
  healthcareIndustryList,
  Role,
  roleList,
  useCaseList,
  workTypeList,
} from "@/redux/features/candidateProfileSlice";

import generateSkillOptionsLists from "@/utils/generateSkillOptionsList";

import { useUpdateCandidateProfile } from "@/mutations/candidate/profile/updateProfile";

const HealthcareIndustryOptions = [
  {
    group: "Clinical Services",
    options: healthcareIndustryList
      .slice(0, 5)
      .map((value) => ({ label: value, value })),
  },
  {
    group: "Technology and Data",
    options: healthcareIndustryList
      .slice(5, 10)
      .map((value) => ({ label: value, value })),
  },
  {
    group: "Specialty Care",
    options: healthcareIndustryList
      .slice(10, 16)
      .map((value) => ({ label: value, value })),
  },
  {
    group: "Healthcare Administration",
    options: healthcareIndustryList
      .slice(16, 20)
      .map((value) => ({ label: value, value })),
  },
  {
    group: "Management and Engagement",
    options: healthcareIndustryList
      .slice(20, 24)
      .map((value) => ({ label: value, value })),
  },
];

const CandidateIdealRoleEditFormDialog = ({
  profileId,
}: {
  profileId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { candidate } = useCandidateQuery(profileId);
  const { updateCandidateProfile, isUpdatingCandidateProfile } =
    useUpdateCandidateProfile(profileId, () => setIsOpen(false));

  const form = useForm<CandidateIdealEngagementFormSchema>({
    resolver: zodResolver(candidateIdealEngagementFormSchema),
    mode: "onChange",
    defaultValues: {
      idealWorkType: candidate?.idealWorkType ?? [],
      idealRoles: candidate?.idealRoles ?? [],
      idealSubindustries: candidate?.idealSubindustries ?? [],
      idealUseCase: candidate?.idealUseCase ?? [],
      idealCompanyHeadcount: candidate?.idealCompanyHeadcount ?? [],
    },
  });

  useEffect(() => {
    form.reset({
      idealWorkType: candidate?.idealWorkType ?? [],
      idealRoles: candidate?.idealRoles ?? [],
      idealSubindustries: candidate?.idealSubindustries ?? [],
      idealUseCase: candidate?.idealUseCase ?? [],
      idealCompanyHeadcount: candidate?.idealCompanyHeadcount ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);

  const onSubmit = (data: CandidateIdealEngagementFormSchema) => {
    const { skillsList } = generateSkillOptionsLists({
      roles: data.idealRoles ?? [],
      list: "top",
      topSkills: data.topSkills ?? [],
      secondarySkills: data.secondarySkills ?? [],
      additionalSkills: data.additionalSkills ?? [],
      maxSelections: 5,
    });

    updateCandidateProfile({
      emails: candidate?.emails ?? [],
      ...data,
      // Remove any skills that are no longer applicable
      topSkills: candidate?.topSkills.filter((skill) =>
        skillsList.includes(skill),
      ),
      secondarySkills: candidate?.secondarySkills.filter((skill) =>
        skillsList.includes(skill),
      ),
      additionalSkills: candidate?.additionalSkills.filter((skill) =>
        skillsList.includes(skill),
      ),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="edit-text self-end">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit Ideal Engagement</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Update your ideal engagement below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="idealRoles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Choose up to 3">
                      Ideal Roles
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        values={field.value ?? []}
                        setValues={field.onChange}
                        options={[...roleList]}
                        max={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idealRoles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Drag to re-order">
                      Ranked Ideal Roles
                    </FormLabel>
                    <FormControl>
                      <IdealOrderSorting
                        values={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idealWorkType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ideal Type of Work</FormLabel>
                    <FormControl>
                      <MultiSelect
                        values={field.value ?? []}
                        setValues={field.onChange}
                        options={[...workTypeList]}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idealSubindustries"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Choose up to 5">
                      Ideal Healthcare Industry
                    </FormLabel>
                    <FormControl>
                      <GroupedMultiSelect
                        values={field.value ?? []}
                        setValues={field.onChange}
                        groupedOptions={[...HealthcareIndustryOptions]}
                        max={5}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idealUseCase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Choose up to 3">
                      Ideal Specialty
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        values={field.value ?? []}
                        setValues={field.onChange}
                        options={[...useCaseList]}
                        max={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idealCompanyHeadcount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Choose up to 3">
                      Ideal Company Size
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        values={field.value ?? []}
                        setValues={field.onChange}
                        options={[...companyEmployeesList]}
                        max={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid || isUpdatingCandidateProfile
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const IdealOrderSorting = ({
  values,
  onChange,
}: {
  values?: Role[];
  onChange: (v: string[]) => void;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (
    event: DragEndEvent,
    list: Role[] | undefined,
    onChange: (v: string[]) => void,
  ): void => {
    if (!list) {
      return;
    }

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = list.indexOf(active.id as Role);
      const newIndex = list.indexOf(over.id as Role);

      onChange(arrayMove(list, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(event) => handleDragEnd(event, values, onChange)}
    >
      <SortableContext
        items={values ?? []}
        strategy={verticalListSortingStrategy}
      >
        {values?.map((id, idx) => <SortableItem key={id} id={id} idx={idx} />)}
      </SortableContext>
    </DndContext>
  );
};

const SortableItem = (props: { key: string; id: string; idx: number }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center"
    >
      <p>
        {props.idx + 1}. {props.id}
      </p>
    </div>
  );
};

export default CandidateIdealRoleEditFormDialog;
